/* eslint-disable */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";

export default function AdminKnowledgeBaseTab() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await adminApi.get("/admin/kb");
    setArticles(data.articles || []);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const startNew = () => setEditing({
    title: "", body: "", category: "General", tags: [],
    is_published: false, show_as_banner: false, banner_text: "",
    banner_priority: "INFORMATIONAL", banner_active: false,
  });

  const save = async () => {
    try {
      await adminApi.post("/admin/kb/upsert", editing);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const deactivate = async (id) => {
    await adminApi.post(`/admin/kb/${id}/deactivate-banner`);
    toast.success("Banner deactivated");
    await load();
  };

  return (
    <div className="space-y-4" data-testid="admin-kb-tab">
      <div className="flex justify-end">
        <Button size="sm" onClick={startNew} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="kb-new-article">
          <Plus className="w-4 h-4 mr-1.5" /> New Article
        </Button>
      </div>
      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
              <th className="py-2 px-3">Title</th>
              <th className="py-2 px-3">Category</th>
              <th className="py-2 px-3">Published</th>
              <th className="py-2 px-3">Banner</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-b border-[var(--rg-border)]/60">
                <td className="py-2 px-3 text-[var(--rg-text-strong)]">{a.title}</td>
                <td className="py-2 px-3 text-[var(--rg-text)]">{a.category}</td>
                <td className="py-2 px-3">
                  {a.is_published ? (
                    <span
                      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-on-sage)] bg-[var(--rg-sage)]"
                      data-testid={`kb-status-live-${a.id}`}
                    >
                      Live
                    </span>
                  ) : (
                    <span
                      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-[var(--rg-text-strong)] bg-[var(--rg-muted)]"
                      data-testid={`kb-status-draft-${a.id}`}
                    >
                      Draft
                    </span>
                  )}
                </td>
                <td className="py-2 px-3">{a.banner_active ? <span className="text-[var(--rg-amber)] font-bold uppercase text-[10px]">{a.banner_priority}</span> : <span className="text-[var(--rg-muted)]">—</span>}</td>
                <td className="py-2 px-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(a)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] text-xs h-7">Edit</Button>
                  {a.banner_active && (
                    <Button size="sm" variant="outline" onClick={() => deactivate(a.id)} className="border-[var(--rg-red)] text-[var(--rg-red)] hover:bg-[var(--rg-red-soft)] text-xs h-7" data-testid={`kb-deactivate-${a.id}`}>
                      Deactivate Banner
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {articles.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-[var(--rg-muted)]">No articles yet. Click &ldquo;New Article&rdquo; to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl" data-testid="kb-editor-modal">
          <DialogHeader>
            <DialogTitle>Edit article</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Label className="text-[var(--rg-text)] text-xs uppercase">Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="kb-title" />
              <Label className="text-[var(--rg-text)] text-xs uppercase">Body</Label>
              <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={6} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="kb-body" />
              <Label className="text-[var(--rg-text)] text-xs uppercase">Category</Label>
              <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" />
              <label className="flex items-center gap-2"><Switch checked={editing.is_published} onCheckedChange={(v) => setEditing({ ...editing, is_published: v })} /> Published</label>
              <label className="flex items-center gap-2"><Switch checked={editing.show_as_banner} onCheckedChange={(v) => setEditing({ ...editing, show_as_banner: v })} data-testid="kb-show-banner" /> Display as Global Dashboard Banner</label>
              {editing.show_as_banner && (
                <div className="space-y-3 border border-[var(--rg-border)] rounded-md p-3 bg-[var(--rg-surface)]">
                  <Label className="text-[var(--rg-text)] text-xs uppercase">Banner text (max 160 chars — {editing.banner_text.length}/160)</Label>
                  <Input value={editing.banner_text} maxLength={160} onChange={(e) => setEditing({ ...editing, banner_text: e.target.value })} className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)]" data-testid="kb-banner-text" />
                  <Label className="text-[var(--rg-text)] text-xs uppercase">Priority</Label>
                  <select value={editing.banner_priority} onChange={(e) => setEditing({ ...editing, banner_priority: e.target.value })} className="bg-[var(--rg-bg)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] text-sm rounded px-2 py-1.5" data-testid="kb-banner-priority">
                    <option value="INFORMATIONAL">INFORMATIONAL (blue)</option>
                    <option value="IMPORTANT">IMPORTANT (yellow)</option>
                    <option value="URGENT">URGENT (red)</option>
                  </select>
                  <label className="flex items-center gap-2"><Switch checked={editing.banner_active} onCheckedChange={(v) => setEditing({ ...editing, banner_active: v })} data-testid="kb-banner-active" /> Activate this banner (deactivates any other active banner)</label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)]">Cancel</Button>
            <Button onClick={save} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="kb-save">Save & Publish Banner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
